/**
 * SpotlightBackdrop - SVG overlay with animated cutout
 *
 * Full-viewport overlay using SVG <mask> to create a spotlight
 * effect around the target element. The cutout has rounded corners
 * and transitions smoothly between positions.
 *
 * When `interactive` is true, a transparent clickable area is rendered
 * over the cutout so clicks pass through to the element underneath.
 */

import React from 'react';
import { ElementRect } from '@/lib/onboardingUtils';

interface SpotlightBackdropProps {
  /** Target element rect for the cutout (null = no cutout, full overlay) */
  targetRect: ElementRect | null;
  /** Whether to show the spotlight cutout */
  showSpotlight: boolean;
  /** Whether this step is interactive (clicks pass through cutout) */
  interactive?: boolean;
  /** Click handler for backdrop area (outside spotlight) */
  onClick?: () => void;
}

const BORDER_RADIUS = 12;

const SpotlightBackdrop: React.FC<SpotlightBackdropProps> = ({
  targetRect,
  showSpotlight,
  interactive,
  onClick,
}) => {
  const showCutout = showSpotlight && targetRect;

  return (
    <>
      <svg
        className="onboarding-backdrop"
        onClick={onClick}
        aria-hidden="true"
      >
        <defs>
          <mask id="onboarding-spotlight-mask">
            {/* White = visible area (shows the dark overlay) */}
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {/* Black = transparent area (the cutout/spotlight) */}
            {showCutout && (
              <rect
                className="onboarding-spotlight-cutout"
                x={targetRect.x}
                y={targetRect.y}
                width={targetRect.width}
                height={targetRect.height}
                rx={BORDER_RADIUS}
                ry={BORDER_RADIUS}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.6)"
          mask="url(#onboarding-spotlight-mask)"
        />
      </svg>
      {/* Interactive: transparent div over cutout to allow clicks through */}
      {interactive && showCutout && (
        <div
          className="onboarding-interactive-zone"
          style={{
            position: 'fixed',
            left: targetRect.x,
            top: targetRect.y,
            width: targetRect.width,
            height: targetRect.height,
            borderRadius: BORDER_RADIUS,
            zIndex: 10001,
            cursor: 'pointer',
            pointerEvents: 'none',
          }}
        />
      )}
    </>
  );
};

export default SpotlightBackdrop;
